import React, { useState, useEffect } from "react";
import { makeStyles } from "@mui/styles";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Tooltip from "@mui/material/Tooltip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import Chip from "@mui/material/Chip";
import Pagination from "@mui/material/Pagination";
import Snackbar from "@mui/material/Snackbar";

import SaveIcon from "@mui/icons-material/SaveAlt";

import moment from "moment";

import { UUID } from "@senn/jsutil";
import { STATUS_COLORS } from "../../../../color/colors";

const DATE_FORMAT = "DD MMM YYYY HH:mm:ss";

const useStyles = makeStyles((theme) => ({
  detailsContainer: {
    borderRadius: 8,
    borderColor: "lightgray",
    borderWidth: 3,
    borderStyle: "solid",
  },
  detailStatusCell: {
    height: "1rem",
    width: "4rem",
  },
  detailsActionsHeader: {
    float: "right",
    marginBottom: 12,
  },
  actionButton: {
    marginLeft: 3,
    marginRight: 3,
  },
  statusCell: {
    height: "1em",
    width: "3em",
  },
  propertyNameCell: {
    width: "30%",
    fontWeight: "bold",
  },
  propertyValueCell: {
    width: "70%",
  },
  chipsContainer: {
    display: "flex",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(0.5),
    },
  },
}));

const getStatusFromLastUpdated = (now, lastUpdated, updateInterval) => {
  const diff = now.diff(moment(lastUpdated), "seconds");
  updateInterval = parseInt(updateInterval || "10");
  const intervals = diff / updateInterval;
  if (intervals < 4) {
    //changes from < 2 to < 4. < 2 was too aggressive in changing state to warning when even when there's a slight delay in updates.
    return "good";
  } else if (intervals < 10) {
    return "warning";
  } else {
    return "bad";
  }
};

const TagLabels = ({ tags }) => {
  const PAGESIZE = 50;

  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(0);

  useEffect(() => {
    setPageCount(Math.ceil(tags.length / PAGESIZE));
  }, [tags.length]);

  const handlePageChange = (_, pnum) => {
    setPage(pnum);
  };

  const classes = useStyles();

  return (
    <div>
      <div className={classes.chipsContainer}>
        {tags.slice((page - 1) * PAGESIZE, page * PAGESIZE).map((tag) => (
          <Chip key={tag} size="small" label={tag} />
        ))}
      </div>
      {tags.length > PAGESIZE && (
        <Pagination
          showFirstButton
          showLastButton
          count={pageCount}
          page={page}
          onChange={handlePageChange}
        />
      )}
    </div>
  );
};

const StackDetails = ({
  stack,
  readOnly,
  updateItem,
  setTitle,
  dirty,
  setDirty,
}) => {
  const classes = useStyles();
  const [toastOpen, setToastOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const devSession = JSON.parse(stack?.dev_session ?? {});
  const [stackName, setStackName] = useState(
    stack?.name || devSession["stack.label"] || ""
  );
  const [stackLocation, setStackLocation] = useState(
    stack?.location || devSession["stack.location"] || ""
  );
  const enableEdit = () => {
    setEditing(true);
  };

  useEffect(() => {
    setTitle("Properties");
  }, [setTitle]);

  async function saveChangesToDB() {
    setDirty(false);
    await updateItem(stack.guuid, { name: stackName, location: stackLocation });
    setToastOpen(true);
  }
  const handleNameChange = (event) => {
    setStackName(event.target.value);
    setDirty(true);
  };

  const handleLocationChange = (event) => {
    setStackLocation(event.target.value);
    setDirty(true);
  };

  const reset = () => {
    setDirty(false);
    setEditing(false);
    setStackName(stack?.name || devSession["stack.label"] || "");
    setStackLocation(stack?.location || devSession["stack.location"] || "");
  };

  const closeToast = () => {
    setToastOpen(false);
    setEditing(false);
  };

  if (stack) {
    const now = moment();
    const bps = devSession["stack.blueprints"];
    const bpsAsStrings = (bps || []).map((bpuu) => new UUID(bpuu).toString());
    const lastUpdated = parseInt(stack.st_time);
    const status = getStatusFromLastUpdated(now, lastUpdated, stack.upd_int);
    return (
      <>
        {!readOnly && (
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <div className={classes.detailsActionsHeader}>
                {dirty ? (
                  <div>
                    <Button
                      className={classes.actionbutton}
                      disabled={toastOpen}
                      color="primary"
                      onClick={saveChangesToDB}
                      startIcon={<SaveIcon />}
                    >
                      Save
                    </Button>
                    <Tooltip
                      title="Current changes will be discarded"
                      placement="top"
                    >
                      <span>
                        <Button
                          className={classes.actionbutton}
                          disabled={toastOpen}
                          style={{ color: "red" }}
                          onClick={reset}
                        >
                          Cancel
                        </Button>
                      </span>
                    </Tooltip>
                  </div>
                ) : (
                  <Button
                    disabled={editing}
                    className={classes.actionbutton}
                    color="primary"
                    onClick={enableEdit}
                  >
                    Edit
                  </Button>
                )}
              </div>
            </Grid>
          </Grid>
        )}
        <div className={classes.detailsContainer}>
          <Table>
            <TableBody>
              <TableRow>
                <TableCell className={classes.propertyNameCell}>Name</TableCell>
                <TableCell className={classes.propertyValueCell}>
                  {editing ? (
                    <Tooltip title="Type Name" placement="top">
                      <FormControl variant="standard" fullWidth={true}>
                        <TextField
                          variant="standard"
                          value={stackName}
                          onChange={handleNameChange}
                          InputLabelProps={{ className: classes.inputLabels }}
                          InputProps={{ className: classes.inputs }}
                          disabled={readOnly}
                        />
                      </FormControl>
                    </Tooltip>
                  ) : (
                    stackName
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.propertyNameCell}>
                  Location
                </TableCell>
                <TableCell className={classes.propertyValueCell}>
                  {editing ? (
                    <Tooltip title="Type Location" placement="top">
                      <FormControl variant="standard" fullWidth={true}>
                        <TextField
                          variant="standard"
                          value={stackLocation}
                          onChange={handleLocationChange}
                          InputLabelProps={{ className: classes.inputLabels }}
                          InputProps={{ className: classes.inputs }}
                          disabled={readOnly}
                        />
                      </FormControl>
                    </Tooltip>
                  ) : (
                    stackLocation
                  )}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.propertyNameCell}>
                  GUUID
                </TableCell>
                <TableCell className={classes.propertyValueCell}>
                  {stack.guuid}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.propertyNameCell}>
                  Last Updated
                </TableCell>
                <TableCell className={classes.propertyValueCell}>
                  {lastUpdated
                    ? moment(lastUpdated).format(DATE_FORMAT)
                    : "---"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.propertyNameCell}>
                  Status
                </TableCell>
                <TableCell className={classes.propertyValueCell}>
                  <div
                    className={classes.detailStatusCell}
                    style={{
                      backgroundColor: STATUS_COLORS[status],
                    }}
                  />
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.propertyNameCell}>
                  Owner
                </TableCell>
                <TableCell className={classes.propertyValueCell}>
                  {stack.g_owner}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.propertyNameCell}>
                  Blueprints
                </TableCell>
                <TableCell className={classes.propertyValueCell}>
                  <div className={classes.chipsContainer}>
                    {bpsAsStrings.map((bp) => (
                      <Chip key={bp} size="small" label={bp} />
                    ))}
                  </div>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.propertyNameCell}>
                  Last Blueprint Update
                </TableCell>
                <TableCell className={classes.propertyValueCell}>
                  {stack.lastbpuptime
                    ? moment(parseInt(stack.lastbpuptime)).format(DATE_FORMAT)
                    : "---"}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell className={classes.propertyNameCell}>Tags</TableCell>
                <TableCell className={classes.propertyValueCell}>
                  <TagLabels
                    tags={((stack.data_config ?? {}).dsitems ?? []).sort()}
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
          <Snackbar
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "center",
            }}
            open={toastOpen}
            autoHideDuration={3000}
            onClose={closeToast}
            message="Updates saved"
          />
        </div>
      </>
    );
  } else {
    return null;
  }
};

export default StackDetails;
